// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';
import type { OnboardingQuestion } from '@shared/advisory/account-workflow/types';

export interface UseQuestionCheckboxesProps {
  question: OnboardingQuestion;
  control: Control<FieldValues>;
  name: Path<FieldValues>;
}

export const useQuestionCheckboxes = ({
  question,
  control,
  name,
}: UseQuestionCheckboxesProps) => {
  const value = useWatch({
    control,
    name,
  });

  const isOptionChecked = useCallback(
    (optionValue: string) => {
      return Array.isArray(value) && value.includes(optionValue);
    },
    [value],
  );

  const handleToggle = useCallback(
    (optionValue: string, onChange: (...event: any[]) => void) => {
      const currentValue = Array.isArray(value) ? value : [];

      if (currentValue.includes(optionValue)) {
        onChange(currentValue.filter((v: string) => v !== optionValue));
      } else {
        onChange([...currentValue, optionValue]);
      }
    },
    [value],
  );

  return {
    isOptionChecked,
    handleToggle,
  };
};
